import { useSyncExternalStore } from 'react';

/** Three looks. "system" follows the browser: Light by day, Graphite when the system is dark.
 *  The pick is kept in the scamp_theme cookie on .serverscamp.com, shared with the site, and in
 *  localStorage for when cookies are blocked; no pick means "system". */
export type Theme = 'light' | 'dark' | 'graphite';
export type ThemePref = Theme | 'system';

export const THEMES: { id: ThemePref; label: string }[] = [
  { id: 'system', label: 'System' },
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'graphite', label: 'Graphite' },
];

const KEY = 'scamp.theme';
const COOKIE = 'scamp_theme';
const SYSTEM_DARK: Theme = 'graphite';
const BAR: Record<Theme, string> = { light: '#ffffff', dark: '#0a0a0a', graphite: '#15181b' };
const media = window.matchMedia('(prefers-color-scheme: dark)');
const subs = new Set<() => void>();

/** The cookie wins: the site may have changed it since this browser last saw the panel. */
function stored(): ThemePref {
  const m = document.cookie.match(/(?:^|; )scamp_theme=(light|dark|graphite|system)/);
  if (m) return m[1] as ThemePref;
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' || v === 'graphite' ? v : 'system';
  } catch { return 'system'; }
}

/** "system" is written too, so it overrides an older pick kept on the other site. */
function writeCookie(p: ThemePref) {
  const host = location.hostname;
  const shared = host === 'serverscamp.com' || host.endsWith('.serverscamp.com');
  const attrs = '; Path=/; SameSite=Lax' + (shared ? '; Domain=.serverscamp.com' : '') + (location.protocol === 'https:' ? '; Secure' : '');
  document.cookie = `${COOKIE}=${p}${attrs}; Max-Age=31536000`;
}

const resolve = (p: ThemePref): Theme => (p === 'system' ? (media.matches ? SYSTEM_DARK : 'light') : p);

let pref: ThemePref = stored();

function apply() {
  const t = resolve(pref);
  document.documentElement.dataset.theme = t;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BAR[t]);
  subs.forEach((f) => f());
}

media.addEventListener('change', () => { if (pref === 'system') apply(); });
window.addEventListener('storage', (e) => { if (e.key === KEY) { pref = stored(); apply(); } });
// A tab of the site may have switched the theme while this one was in the background
document.addEventListener('visibilitychange', () => { if (!document.hidden) { pref = stored(); apply(); } });

export function setTheme(p: ThemePref) {
  pref = p;
  try { if (p === 'system') localStorage.removeItem(KEY); else localStorage.setItem(KEY, p); } catch { /* private mode: cookie only */ }
  writeCookie(p);
  apply();
}

let snap = { pref, theme: resolve(pref) };
function read() {
  const theme = (document.documentElement.dataset.theme as Theme) || 'light';
  if (snap.pref !== pref || snap.theme !== theme) snap = { pref, theme };
  return snap;
}

/** The chosen preference and the theme it resolves to. */
export function useTheme() {
  return useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, read);
}

export const isDark = (t: Theme) => t !== 'light';
